import { produce } from 'immer';

import { assertUnlessInProduction } from '@paragon/client-tools';
import { AbsoluteTopOrBottomCut, PlaneCut, Point2D, SolidData, TopOrBottomCut } from '@paragon/contract-library';
import { centerPoint3D, getNormalDirection, LineSegment2D, Plane3D, Point3D } from '@paragon/geometry-library';
import { assertExhaustiveSwitch, ModifierKey, ModifierKeys, notNullish } from '@paragon/utilities';

import { movePointInDirection, PointMovement } from '../../../library/layout-geometry/directions';
import { createSnapDerivedGeometry } from '../../../library/layout-geometry/snap-derived-geometry';
import {
  applyPickedLineToInsertionWorkLine,
  applyPickedPointToInsertionWorkLine,
} from '../../../library/layout-geometry/work-lines';
import { EntitySnapTarget } from '../../../library/layout-snapping/EntitySnapTarget';
import { SnapMousePositionResult } from '../../../library/layout-snapping/point-snapping/snapMousePosition';
import { getSingleSelectedItemIdentifierOrNull } from '../../../library/selection/selectors';
import { isBearingEnvelopeSelectableType } from '../../../library/selection/types';
import { focusCameraToVisibleSnappingGuideLines } from '../../../three/ThreeJsSceneActions';
import { GlobalThreeJSDomObjectEvent } from '../../../three/util/events/types';
import { getSelectedItemEntries } from '../../selectors/getSelectedItemSelector';
import { getAbsolutePlaneElevationFromActiveLevel } from '../../selectors/layoutDataSelectors';
import {
  getLayoutPointPickingTarget,
  getPickingObjectInfo,
  isPickedPointMovementRecipientActive,
} from '../../selectors/pickingSelectors';
import {
  __UNSAFE_pickedPointMovementPending,
  addCutOnInsertingTrussEnvelope,
  applicationOnPickingLayoutPoint,
  clearPickingObject,
  LayoutPointPickingTarget,
  pickedPointMovementCleared,
  PickedPointMovementRecipient,
  setInsertionWorkLine,
  setWorkLineInsertionFirstSelection,
  TrussEnvelopeTargetInfo,
  updateInsertingBearingEnvelope,
  updateInsertingCeilingPlane,
  updateInsertingCeilingTray,
  updateInsertingRoofPlane,
  updateInsertionFeatureArea,
} from '../../slices/applicationSlice';
import { getBearingEnvelopeInsertionSettings, layoutSlice } from '../../slices/layoutSlice';
import { setFasciaHeightReferencePlaneGuid } from '../../slices/panelDataSlice';
import { snappingUnderlaySlice } from '../../slices/snappingUnderlaySlice';
import { AsyncThunkActionVoid, ThunkActionVoid } from '../thunkActions';
import {
  editBearingEnvelopes,
  finishInsertingBearingEnvelope,
  getBearingEnvelopeInsertionCompletionAction,
} from './bearingEnvelopeActions';
import { editCeilingPlanes, finishInsertingCeilingPlane, handleCeilingPlaneCutPicked } from './ceilingPlaneActions';
import { editCeilingTrays } from './ceilingTrayActions';
import { setFeatureAreaPosition } from './featureAreaActions';
import {
  editActiveFeaturePatternContainer,
  handleFeaturePatternContainerBoundarySegmentPicked,
} from './featurePatternContainerActions';
import { editRoofPlanes, finishInsertingRoofPlane, handleRoofPlaneCutPicked } from './roofPlaneActions';
import { saveSnappingUnderlay } from './snappingUnderlayActions';
import {
  applyPendingTransformationIfFullyDefined,
  setMirrorCenterLine,
  setMovementDestinationPoint,
  setMovementOriginPoint,
} from './transformationActions';
import {
  editExistingTrussEnvelopes,
  editInsertionTrussEnvelope,
  getTargetedTrussEnvelopes,
  insertAndRepeatTrussEnvelopeIfFullyDefined,
  topOrBottomCutEquals,
  trimOrExtendEnvelopes,
  updateInsertionCornerSet,
} from './trussEnvelopeActions';

/**
 * Note that this action does not advance picking: it does not move on to the next point, complete the operation once
 *   all of its points are defined (eg. insert and repeat, or applying a movement), or exit picking. You should use
 *   `handlePickedPointInLayoutMode` instead unless your intent is specifically to surgically edit a target point
 *   without the usual consequences.
 */
export const applyLayoutPointToTargetWithoutAdvancing =
  (target: LayoutPointPickingTarget, point: Point2D): AsyncThunkActionVoid =>
  async (dispatch, getState) => {
    const state = getState();

    const insertionInfo = state.application.insertingObjectInfo;
    const activeTransformation = state.application.transformingObjectsInfo;

    const selectedItem = getSingleSelectedItemIdentifierOrNull(state);

    switch (target) {
      case 'insertion-envelope-left':
      case 'insertion-envelope-right': {
        const changes = target === 'insertion-envelope-left' ? { leftPoint: point } : { rightPoint: point };

        if (insertionInfo?.type === 'trussEnvelope') dispatch(editInsertionTrussEnvelope(changes));
        break;
      }

      case 'editing-envelope-left':
      case 'editing-envelope-right': {
        if (selectedItem?.type !== 'trussEnvelope') break;

        const changes = target === 'editing-envelope-left' ? { leftPoint: point } : { rightPoint: point };

        await dispatch(
          editExistingTrussEnvelopes({
            guid: selectedItem.guid,
            editedProperties: { ...changes, componentDesignGuid: null },
          }),
        );

        break;
      }

      case 'bearing-envelope-left':
      case 'bearing-envelope-right': {
        const changes = target === 'bearing-envelope-left' ? { leftPoint: point } : { rightPoint: point };

        if (insertionInfo?.type === 'bearingEnvelope') {
          dispatch(updateInsertingBearingEnvelope({ bearingType: insertionInfo.data.bearingType, changes }));
        } else if (selectedItem && isBearingEnvelopeSelectableType(selectedItem.type)) {
          await dispatch(editBearingEnvelopes({ guid: selectedItem.guid, editedProperties: changes }));
        }

        break;
      }

      case 'ceiling-tray-position':
        if (insertionInfo?.type === 'ceilingTray') {
          dispatch(updateInsertingCeilingTray({ position: point }));
        } else if (selectedItem?.type === 'ceilingTray') {
          await dispatch(editCeilingTrays([{ guid: selectedItem.guid, editedProperties: { position: point } }]));
        }

        break;

      case 'feature-position':
        if (insertionInfo?.type === 'featureArea') {
          dispatch(
            updateInsertionFeatureArea(
              produce(insertionInfo.data.value, (draft) => {
                draft.area.position = point;
              }),
            ),
          );
        } else if (selectedItem?.type === 'featureArea') {
          dispatch(setFeatureAreaPosition(selectedItem.guid, point));
        }

        break;

      case 'transformation-origin':
      case 'transformation-destination':
        if (activeTransformation?.type === 'movement') {
          dispatch(
            target === 'transformation-origin' ? setMovementOriginPoint(point) : setMovementDestinationPoint(point),
          );
        }

        break;

      case 'transformation-center':
        if (activeTransformation?.type === 'rotation') {
          dispatch({
            type: 'application/transformationBeganOrUpdated',
            payload: { ...activeTransformation, center: point },
          });
        }

        break;

      case 'uploaded-lines-origin': {
        const underlayGuid = selectedItem?.type === 'snappingUnderlay' ? selectedItem.guid : undefined;
        const underlay = underlayGuid ? state.layout.snappingUnderlays[underlayGuid] : undefined;

        if (underlay) {
          await dispatch(
            saveSnappingUnderlay({
              ...underlay,
              translate: {
                x: underlay.translate.x - point.x,
                y: underlay.translate.y - point.y,
              },
            }),
          );

          focusCameraToVisibleSnappingGuideLines();
        }

        break;
      }

      case 'uploaded-lines-scale-base':
        dispatch(snappingUnderlaySlice.actions.scalePickingBasePointSet(point));
        break;

      case 'uploaded-lines-scale-end':
        dispatch(snappingUnderlaySlice.actions.scalePickingEndPointSet(point));
        break;

      case 'insertion-work-line-point-angle-base':
      case 'insertion-work-line-point-to-point-base':
      case 'insertion-work-line-point-to-point-end':
      case 'insertion-work-line-perpendicular-point': {
        if (insertionInfo?.type !== 'workLine' || insertionInfo.draft.phase !== 'reviewing') break;

        const updatedLine = applyPickedPointToInsertionWorkLine(insertionInfo.draft.line, target, point);
        if (updatedLine != null) dispatch(setInsertionWorkLine(updatedLine));

        break;
      }

      default:
        assertExhaustiveSwitch(target);
    }
  };

const advancePickingAfterPickedPoint =
  (target: LayoutPointPickingTarget, modifiers: ModifierKeys | undefined): AsyncThunkActionVoid =>
  async (dispatch, getState) => {
    const insertionInfo = getState().application.insertingObjectInfo;

    switch (target) {
      case 'insertion-envelope-left':
      case 'insertion-envelope-right': {
        if (insertionInfo?.type !== 'trussEnvelope') return;

        const { leftPoint, rightPoint } = insertionInfo.data;

        if (leftPoint == null) {
          dispatch(applicationOnPickingLayoutPoint({ target: 'insertion-envelope-left' }));
        } else if (rightPoint == null) {
          dispatch(applicationOnPickingLayoutPoint({ target: 'insertion-envelope-right' }));
        } else {
          dispatch(clearPickingObject());

          if (modifiers?.altKey) {
            await dispatch(insertAndRepeatTrussEnvelopeIfFullyDefined());
          }
        }

        break;
      }

      case 'bearing-envelope-left':
      case 'bearing-envelope-right': {
        if (insertionInfo?.type !== 'bearingEnvelope') {
          dispatch(clearPickingObject());
          break;
        }

        const { bearingType, leftPoint, rightPoint } = insertionInfo.data;

        if (leftPoint == null) {
          dispatch(applicationOnPickingLayoutPoint({ target: 'bearing-envelope-left' }));
        } else if (rightPoint == null) {
          dispatch(applicationOnPickingLayoutPoint({ target: 'bearing-envelope-right' }));
        } else {
          dispatch(clearPickingObject());

          const completionAction = getBearingEnvelopeInsertionCompletionAction(
            modifiers,
            getBearingEnvelopeInsertionSettings(getState().layout, bearingType).autoInsertOnSecondPoint
              ? 'repeat'
              : null,
          );

          if (completionAction === 'repeat') {
            await dispatch(finishInsertingBearingEnvelope({ postInsertionAction: 'repeat' }));
          } else if (completionAction === 'finish') {
            await dispatch(finishInsertingBearingEnvelope());
          }
        }

        break;
      }

      case 'ceiling-tray-position':
        if (insertionInfo?.type === 'ceilingTray') dispatch(clearPickingObject());
        break;

      case 'transformation-origin':
      case 'transformation-destination':
        dispatch(clearPickingObject());
        await dispatch(applyPendingTransformationIfFullyDefined());
        break;

      case 'editing-envelope-left':
      case 'editing-envelope-right':
      case 'feature-position':
      case 'transformation-center':
      case 'uploaded-lines-origin':
      case 'uploaded-lines-scale-base':
      case 'uploaded-lines-scale-end':
      case 'insertion-work-line-point-angle-base':
      case 'insertion-work-line-point-to-point-base':
      case 'insertion-work-line-point-to-point-end':
      case 'insertion-work-line-perpendicular-point':
        dispatch(clearPickingObject());
        break;

      default:
        assertExhaustiveSwitch(target);
    }
  };

export const handlePickedPointInLayoutMode =
  (
    point: Point2D,

    options?: {
      // Used by some picking modes to inform follow-up operations (eg. insert and repeat). Set this whenever possible.
      modifiers?: ModifierKeys;
    },
  ): AsyncThunkActionVoid =>
  async (dispatch, getState) => {
    const target = getLayoutPointPickingTarget(getState());
    if (target == null) return;

    try {
      await dispatch(applyLayoutPointToTargetWithoutAdvancing(target, point));
    } finally {
      await dispatch(advancePickingAfterPickedPoint(target, options?.modifiers));
    }
  };

/**
 * The modifier key that, when held while picking a layout point, prompts the user to move the picked point by a
 *   distance in a direction before it is applied.
 */
export const PICKED_POINT_MOVEMENT_MODIFIER: ModifierKey = 'shiftKey';

/**
 * Holds a picked point without applying it, so the user can move it before it is applied. Picking remains active while
 *   the point is pending.
 */
export const beginPickedPointMovement =
  (
    recipient: PickedPointMovementRecipient,
    snapResult: SnapMousePositionResult,
    options: { clientPosition: Point2D; modifiers: ModifierKeys },
  ): ThunkActionVoid =>
  (dispatch, getState) => {
    if (!isPickedPointMovementRecipientActive(getState(), recipient)) return;

    const { altKey, ctrlKey, shiftKey } = options.modifiers;

    dispatch(
      __UNSAFE_pickedPointMovementPending({
        recipient,
        point: snapResult.point.toObject(),
        snapType: snapResult.type,
        clientPosition: { x: options.clientPosition.x, y: options.clientPosition.y },
        modifiers: { altKey, ctrlKey, shiftKey },
      }),
    );
  };

/**
 * Applies the pending picked point (see `beginPickedPointMovement`) moved by `movement`.
 */
export const finishPickedPointMovement =
  (movement: PointMovement): AsyncThunkActionVoid =>
  async (dispatch, getState) => {
    const pending = getState().application.pendingPickedPointMovement;
    dispatch(pickedPointMovementCleared());

    if (pending == null) return;

    const point = movePointInDirection(pending.point, movement);

    switch (pending.recipient.type) {
      case 'layoutPointPicking':
        await dispatch(handlePickedPointInLayoutMode(point, { modifiers: pending.modifiers }));
        break;

      case 'workLineFirstSelection':
        dispatch(setWorkLineInsertionFirstSelection({ type: 'point', point }));
        break;

      default:
        assertExhaustiveSwitch(pending.recipient);
    }
  };

export const handlePickedLineInLayoutMode =
  (
    entitySnap: EntitySnapTarget,
    options?: {
      // Used by some picking modes to inform follow-up operations. Set this whenever possible.
      event?: GlobalThreeJSDomObjectEvent;
    },
  ): AsyncThunkActionVoid =>
  async (dispatch, getState) => {
    const state = getState();
    const { insertingObjectInfo } = state.application;
    const pickingObjectInfo = getPickingObjectInfo(state);

    const selectedItem = getSingleSelectedItemIdentifierOrNull(state);

    if (pickingObjectInfo?.type !== 'pickingLayoutLine') {
      throw new Error('handlePickedLineInLayoutMode called when not picking layout line. Something is broken.');
    }

    switch (pickingObjectInfo.target) {
      case 'envelope-trim-or-extend-line': {
        const selectedItems = getSelectedItemEntries(getState());
        const trussEnvelopeGuids = selectedItems
          .map(({ item, type }) => (type === 'trussEnvelope' ? item.guid : null))
          .filter(notNullish);
        const bearingEnvelopeGuids = selectedItems
          .map(({ item, type }) => (isBearingEnvelopeSelectableType(type) ? item.guid : null))
          .filter(notNullish);

        if (!pickingObjectInfo.repeat) dispatch(clearPickingObject());

        await dispatch(
          trimOrExtendEnvelopes({ trussEnvelopeGuids, bearingEnvelopeGuids }, entitySnap.getDefiningLine()),
        );

        break;
      }

      case 'parallel-snapping-base-line': {
        dispatch(layoutSlice.actions.trussEnvelopeSnappingTargetLocked(entitySnap.toObject()));
        dispatch(clearPickingObject());

        break;
      }

      case 'roof-plane-definition': {
        let repeating = false;

        const { bearingEnvelopes, roofPlaneInsertionSettings } = state.layout;

        const elevationFromActiveLevel = getAbsolutePlaneElevationFromActiveLevel(state);
        const editedProperties = createSnapDerivedGeometry(entitySnap, {
          primaryBearingSide: 'front',
          bearingEnvelopes,
          defaultElevation: elevationFromActiveLevel ?? roofPlaneInsertionSettings.defaultElevationIfAbsolute,
          forceAbsolute: options?.event?.metadata.shiftKey,
        }).asPlaneGeometryProperties();

        if (insertingObjectInfo?.type === 'roofPlane') {
          dispatch(updateInsertingRoofPlane(editedProperties));

          if (options?.event?.metadata.altKey) {
            repeating = true;
            await dispatch(finishInsertingRoofPlane({ postInsertionAction: 'repeat' }));
          }
        } else if (selectedItem?.type === 'roofPlane') {
          await dispatch(editRoofPlanes({ guid: selectedItem.guid, editedProperties }));
        } else {
          throw new Error('Roof plane definition line picked in invalid state—neither inserting or editing plane.');
        }

        if (!repeating) dispatch(clearPickingObject());

        break;
      }

      case 'ceiling-plane-definition': {
        let repeating = false;

        const { bearingEnvelopes, ceilingPlaneInsertionSettings } = state.layout;

        const editedProperties = createSnapDerivedGeometry(entitySnap, {
          primaryBearingSide: 'back',
          bearingEnvelopes,
          defaultElevation: ceilingPlaneInsertionSettings.defaultElevationIfAbsolute,
          forceAbsolute: options?.event?.metadata.shiftKey,
        }).asPlaneGeometryProperties();

        if (insertingObjectInfo?.type === 'ceilingPlane') {
          dispatch(updateInsertingCeilingPlane(editedProperties));

          if (options?.event?.metadata.altKey) {
            repeating = true;
            await dispatch(finishInsertingCeilingPlane({ postInsertionAction: 'repeat' }));
          }
        } else if (selectedItem?.type === 'ceilingPlane') {
          await dispatch(editCeilingPlanes({ guid: selectedItem.guid, editedProperties }));
        } else {
          throw new Error('Ceiling plane definition line picked in invalid state—neither inserting or editing plane.');
        }

        if (!repeating) dispatch(clearPickingObject());

        break;
      }

      case 'feature-pattern-container-boundary': {
        dispatch(handleFeaturePatternContainerBoundarySegmentPicked(entitySnap));

        // this mode does not auto-clear by design; the user will indicate when they are done picking boundary segments

        break;
      }

      case 'feature-pattern-container-slope-base-edge': {
        if (!entitySnap.isSegment()) {
          throw new Error('Infinite line selected as "edge to slope from" for feature pattern container.');
        }

        const edgeToSlopeFrom = (entitySnap.definition as LineSegment2D).toObject();
        dispatch(
          editActiveFeaturePatternContainer((container) =>
            container.slopeSettings
              ? { slopeSettings: { ...container.slopeSettings, edgeToSlopeFrom } }
              : { slopeSettings: { topSlopeInDegrees: 0, bottomSlopeInDegrees: 0, edgeToSlopeFrom } },
          ),
        );

        dispatch(clearPickingObject());

        break;
      }

      case 'transformation-center': {
        const activeTransformation = state.application.transformingObjectsInfo;
        if (activeTransformation?.type !== 'mirror') return;

        dispatch(setMirrorCenterLine(entitySnap.toObject()));
        dispatch(applyPendingTransformationIfFullyDefined());

        dispatch(clearPickingObject());

        break;
      }

      case 'insertion-work-line-parallel-base-line':
      case 'insertion-work-line-perpendicular-base-line':
      case 'insertion-work-line-division-line-1':
      case 'insertion-work-line-division-line-2':
      case 'insertion-work-line-exact-base-line': {
        if (insertingObjectInfo?.type !== 'workLine') break;
        if (insertingObjectInfo.draft.phase !== 'reviewing') break;

        const updatedLine = applyPickedLineToInsertionWorkLine(
          insertingObjectInfo.draft.line,
          pickingObjectInfo.target,
          entitySnap.toObject(),
        );
        if (updatedLine == null) break;

        dispatch(setInsertionWorkLine(updatedLine));

        dispatch(clearPickingObject());

        break;
      }

      default:
        assertExhaustiveSwitch(pickingObjectInfo);
    }
  };

export const handleRoofFacePicked =
  (targetInfo: TrussEnvelopeTargetInfo, roofSolid: SolidData, faceIndex: number): AsyncThunkActionVoid =>
  async (dispatch, getState) => {
    const state = getState();
    const pickingObjectInfo = getPickingObjectInfo(state);
    if (pickingObjectInfo?.type !== 'pickingRoofFace') throw new Error('Invalid picking state.');

    if (!pickingObjectInfo.ordered) dispatch(clearPickingObject());

    const faceVertices = roofSolid.faces[faceIndex].outer.map((vertexIndex) =>
      Point3D.fromObject(roofSolid.vertices[vertexIndex]),
    );
    const normalDirection = getNormalDirection(faceVertices).toObject();

    const cut = {
      type: 'Absolute' as const,
      plane: {
        basePoint: centerPoint3D(faceVertices).toObject(),
        normalDirection,
      },
    };

    await dispatch(
      addCutToTrussEnvelopeTarget(targetInfo, pickingObjectInfo.ordered, cut, pickingObjectInfo.cutOrientation),
    );
  };

export const handleLinePickedDuringPlanePicking =
  (pickedLine: EntitySnapTarget): AsyncThunkActionVoid =>
  async (dispatch, getState) => {
    const pickingObjectInfo = getPickingObjectInfo(getState());

    if (pickingObjectInfo?.type !== 'pickingPlane') {
      throw new Error('Invalid picking state.');
    }

    const pickedCut: PlaneCut = {
      type: 'AbsoluteVertical',
      segment: new LineSegment2D(...pickedLine.getDefiningPoints()).toObject(),
      isInfinite: !pickedLine.isSegment(),
    };

    switch (pickingObjectInfo.targetInfo.type) {
      case 'ceiling-cut':
        await dispatch(handleCeilingPlaneCutPicked(pickedCut));
        return;

      case 'roof-cut':
        await dispatch(handleRoofPlaneCutPicked(pickedCut));
        return;

      case 'fascia-height-reference':
        assertUnlessInProduction('You should not be able to pick a line while choosing a fascia height reference.');
        return;

      case 'truss-envelope-top-cut':
      case 'truss-envelope-bottom-cut':
        assertUnlessInProduction('You should not be able to pick a line while choosing a truss envelope cut.');
        return;

      case 'truss-envelope-ordered-top-cut':
      case 'truss-envelope-ordered-bottom-cut':
        const orientation = pickingObjectInfo.targetInfo.type === 'truss-envelope-ordered-top-cut' ? 'top' : 'bottom';
        const ordered = true;
        const segment = new LineSegment2D(...pickedLine.getDefiningPoints());
        const absolutePlaneCut: AbsoluteTopOrBottomCut = {
          type: 'Absolute',
          plane: Plane3D.fromPerpendicularDirectionAndPoint(
            segment.direction.rotateQuarterTurn().to3D()!,
            segment.midpoint.to3D(),
          ).toObject(),
        };
        await dispatch(
          addCutToTrussEnvelopeTarget(
            pickingObjectInfo.targetInfo.trussEnvelopeTargetInfo,
            ordered,
            absolutePlaneCut,
            orientation,
          ),
        );
        return;

      case 'corner-set-plane-1':
      case 'corner-set-plane-2':
        // Once the corner set tool supports roof container layouts, this will likely change.
        assertUnlessInProduction('You should not be able to pick a line while choosing a corner set plane.');
        return;

      default:
        assertExhaustiveSwitch(pickingObjectInfo.targetInfo);
    }
  };

export const handlePlanePicked =
  (pickedPlaneGuid: string): AsyncThunkActionVoid =>
  async (dispatch, getState) => {
    const pickingObjectInfo = getPickingObjectInfo(getState());

    if (pickingObjectInfo?.type !== 'pickingPlane') {
      throw new Error('Invalid picking state.');
    }

    const { targetInfo } = pickingObjectInfo;

    switch (targetInfo.type) {
      case 'fascia-height-reference':
        dispatch(clearPickingObject());
        dispatch(setFasciaHeightReferencePlaneGuid(pickedPlaneGuid));

        return;

      case 'truss-envelope-top-cut':
      case 'truss-envelope-bottom-cut':
        dispatch(clearPickingObject());
      case 'truss-envelope-ordered-top-cut':
      case 'truss-envelope-ordered-bottom-cut':
        const cut: TopOrBottomCut | null =
          pickedPlaneGuid in getState().layout.roofPlanes
            ? { type: 'RoofPlane', roofPlaneGuid: pickedPlaneGuid }
            : pickedPlaneGuid in getState().layout.ceilingPlanes
              ? { type: 'CeilingPlane', ceilingPlaneGuid: pickedPlaneGuid }
              : null;
        if (!cut) throw new Error('Could not find picked plane');
        const ordered = targetInfo.type.includes('ordered');
        const cutOrientation = targetInfo.type.includes('top') ? 'top' : 'bottom';
        await dispatch(addCutToTrussEnvelopeTarget(targetInfo.trussEnvelopeTargetInfo, ordered, cut, cutOrientation));

        return;

      case 'corner-set-plane-1':
      case 'corner-set-plane-2':
        dispatch(clearPickingObject());
        dispatch(
          updateInsertionCornerSet({
            [targetInfo.type === 'corner-set-plane-1' ? 'plane1Guid' : 'plane2Guid']: pickedPlaneGuid,
          }),
        );
        return;
    }

    const pickedCut: PlaneCut = {
      type: 'AgainstPlane',
      cuttingPlaneGuid: pickedPlaneGuid,
    };

    switch (targetInfo.type) {
      case 'ceiling-cut':
        await dispatch(handleCeilingPlaneCutPicked(pickedCut));
        break;

      case 'roof-cut':
        await dispatch(handleRoofPlaneCutPicked(pickedCut));
        break;

      default:
        assertExhaustiveSwitch(targetInfo);
    }
  };

const addCutToTrussEnvelopeTarget =
  (
    targetInfo: TrussEnvelopeTargetInfo,
    ordered: boolean,
    cut: TopOrBottomCut,
    cutOrientation: 'top' | 'bottom',
  ): AsyncThunkActionVoid =>
  async (dispatch, getState) => {
    if (targetInfo.target === 'insertionTrussEnvelope') {
      if (ordered) throw new Error('Adding ordered plane cuts to the insertion envelope is unsupported');
      dispatch(addCutOnInsertingTrussEnvelope({ cut, cutOrientation }));
      return;
    }

    const trussEnvelopes = getTargetedTrussEnvelopes(targetInfo, getState().layout.trussEnvelopes);

    let envelopesToCut = trussEnvelopes;
    if (!ordered) {
      const cutsProperty = cutOrientation === 'top' ? 'topCuts' : 'bottomCuts';
      envelopesToCut = trussEnvelopes.filter(
        (envelope) => !envelope[cutsProperty].some((existingCut) => topOrBottomCutEquals(cut, existingCut)),
      );
    }

    if (envelopesToCut.length === 0) return;

    if (ordered) {
      const cutsProperty = cutOrientation === 'top' ? 'orderedTopCuts' : 'orderedBottomCuts';
      await dispatch(
        editExistingTrussEnvelopes(
          envelopesToCut.map((trussEnvelope) => ({
            guid: trussEnvelope.guid,
            editedProperties: {
              [cutsProperty]: { ...trussEnvelope[cutsProperty], cuts: [...trussEnvelope[cutsProperty].cuts, cut] },
            },
          })),
        ),
      );
    } else {
      const cutsProperty = cutOrientation === 'top' ? 'topCuts' : 'bottomCuts';
      await dispatch(
        editExistingTrussEnvelopes(
          envelopesToCut.map((trussEnvelope) => ({
            guid: trussEnvelope.guid,
            editedProperties: {
              [cutsProperty]: [...trussEnvelope[cutsProperty], cut],
            },
          })),
        ),
      );
    }
  };
